import { HttpInterceptorFn } from '@angular/common/http';
import { inject } from '@angular/core';
import { Router } from '@angular/router';
import { EMPTY } from 'rxjs';
import { centralBaseUrl } from '../../constants/base-endpoints.constants';
import { AppStorage } from '../../enums/app-storage';
import { CookieService } from '../../services/cookie/cookie.service';
import { AuthService } from '../../services/auth/auth.service';
import { LogoutHelperService } from '../../helpers/logout-helper/logout-helper.service';
import {
  resolveAppUrl,
  resolvePortal,
} from '../../helpers/portal/is-vendor-portal-url';
import { ToasterService } from '../../../../ui-components/services/toast/toast.service';

const BASE_URL_PLACEHOLDER = '{baseUrl}';

const isValidApiBaseUrl = (url: string): boolean => {
  try {
    const protocol = new URL(url).protocol;
    return protocol === 'http:' || protocol === 'https:';
  } catch {
    return false;
  }
};

const isRequestWithinBaseUrl = (
  requestUrl: string,
  baseUrl: string
): boolean => {
  try {
    const parsedRequestUrl = new URL(requestUrl);
    const parsedBaseUrl = new URL(baseUrl);
    const basePath = parsedBaseUrl.pathname.endsWith('/')
      ? parsedBaseUrl.pathname.slice(0, -1)
      : parsedBaseUrl.pathname;

    return (
      parsedRequestUrl.origin === parsedBaseUrl.origin &&
      (parsedRequestUrl.pathname === basePath ||
        parsedRequestUrl.pathname.startsWith(`${basePath}/`))
    );
  } catch {
    return false;
  }
};

export const authInterceptor: HttpInterceptorFn = (req, next) => {
  const cookieService = inject(CookieService);
  const toastService = inject(ToasterService);
  const authService = inject(AuthService);
  const logoutHelper = inject(LogoutHelperService);
  const router = inject(Router);
  const storedBaseUrl = authService.baseUrl();
  const baseUrl =
    storedBaseUrl && isValidApiBaseUrl(storedBaseUrl) ? storedBaseUrl : '';
  const resolvedUrl = baseUrl
    ? req.url.replace(BASE_URL_PLACEHOLDER, baseUrl)
    : req.url;
  const trustedBaseUrls = [baseUrl, centralBaseUrl].filter(Boolean);
  const isApiRequest = trustedBaseUrls.some(trustedBaseUrl =>
    isRequestWithinBaseUrl(resolvedUrl, trustedBaseUrl)
  );

  if (!isApiRequest) {
    if (resolvedUrl.includes(BASE_URL_PLACEHOLDER)) {
      toastService.add(
        'Warning: Your session has expired. Please log in again.',
        '',
        '',
        'error',
        true,
        false
      );
      logoutHelper.handleLogout();
      return EMPTY;
    }
    return next(req);
  }

  const token =
    cookieService.get(AppStorage.token) ??
    localStorage.getItem(AppStorage.tempToken) ??
    '';
  const accessKey = sessionStorage.getItem(AppStorage.accessKey) ?? '';
  const language = cookieService.get(AppStorage.language) ?? 'en';
  const portal = resolvePortal(resolveAppUrl(router.url));
  let headers = req.headers
    .set('Authorization', `Bearer ${token}`)
    .set('X-Access-Key', accessKey);

  if (portal) {
    headers = headers.set('portal', portal);
  }

  if (!headers.has('Accept-Language')) {
    headers = headers.set('Accept-Language', language);
  }

  return next(req.clone({ url: resolvedUrl, headers }));
};
